import { message } from 'antd'
import { routerMiddleware } from 'connected-react-router'
import _ from 'lodash'
import keyMirror from 'pkeymirror'
import { apiMiddleware } from 'redux-api-middleware'
import { getAppSalt, getCountries, getThemeVariables, verifyOTP } from 'store/siteConfig'
import { getUUID, recordCountlyEvent } from 'utils'
import callAPI from 'utils/callAPI'
import { updateAliFingerprint } from 'utils/initAliFingerprint'
import { encrypt } from 'utils/rsa'
import {
  applyDccSelectionToPayBody,
  DCC_INFO_CHOICE_ACCEPT, DCC_INFO_CHOICE_DECLINE,
  parseDccOfferOrNull,
  resolveMerchantPayAmountForDcc,
  shouldAttemptDccCheck,
} from 'utils/dccHelpers'

// ------------------------------------
// Constants
// ------------------------------------
export const PAY_PAGE_MAIN = keyMirror(
  {
    INIT_REQUEST: null,
    INIT_SUCCESS: null,
    HANDLE_REQUEST: null,
    HANDLE_SUCCESS: null,
    TOGGLE_FETCH_STATUS: null,
    SET_DCC_PENDING: null,
    CLEAR_DCC_PENDING: null,
  },
  'PAY_PAGE_MAIN'
)

function finalizeMainBankForm(dispatch, payload) {
  const { bankForm } = payload || {}
  if (!bankForm) {
    throw new Error(' ')
  }
  if (/^http/.test(bankForm)) {
    window.location.href = bankForm
    return
  }
  document.open()
  document.write(bankForm)
  document.close()
}

/**
 * DCC after cache-card-info — backend requires cardToken (e.g. CCT:…). Never throws; failures → null.
 * loginToken is merged in header.js when session has it (same orderToken).
 */
export function checkDccAvailabilityWithCardToken(orderToken, cardToken) {
  return (dispatch) => {
    return dispatch(
      callAPI({
        dispatch,
        method: 'POST',
        body: { orderToken, cardToken },
        endpoint: 'cashierii/pay/v1/unauth/check-dcc-availability',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success: function() {},
        error: function() {},
        hideProgress: true,
        errControl: false,
      })
    )
      .then((action) => parseDccOfferOrNull((action && action.payload) || {}))
      .catch(() => null)
  }
}

export function checkDccAvailabilitySaved(orderToken, payMethodShortNo) {
  return (dispatch) => {
    const loginToken = sessionStorage[orderToken]
    return dispatch(
      callAPI({
        dispatch,
        method: 'POST',
        body: {
          orderToken,
          loginToken,
          payMethodShortNo,
        },
        endpoint: 'cashierii/pay/v1/unauth/check-dcc-availability',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success: function() {},
        error: function() {},
        hideProgress: true,
        errControl: false,
      })
    )
      .then((action) => parseDccOfferOrNull((action && action.payload) || {}))
      .catch(() => null)
  }
}

export function abandonDccPayment() {
  return (dispatch) => {
    dispatch({ type: PAY_PAGE_MAIN.CLEAR_DCC_PENDING })
    return dispatch(toggleLoading(false))
  }
}

export function completeDccPayment(choice) {
  return (dispatch, getState) => {
    const pending = getState().payPageMainState.dccPending
    if (!pending) {
      return Promise.resolve()
    }
    dispatch({ type: PAY_PAGE_MAIN.CLEAR_DCC_PENDING })
    dispatch(toggleLoading(true))

    if (pending.flow === 'CARD_TOKEN') {
      const body = { ...pending.cardTokenPayBody }
      if (pending.dccQuoteId) {
        if (choice === 'DCC') {
          applyDccSelectionToPayBody(body, pending, DCC_INFO_CHOICE_ACCEPT)
        } else {
          applyDccSelectionToPayBody(body, pending, DCC_INFO_CHOICE_DECLINE)
        }
      }
      return dispatch(
        callAPI({
          dispatch,
          method: 'POST',
          body,
          endpoint: 'cashierii/pay/v1/unauth/card-token-pay',
          requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
          successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
          success: function() {},
        })
      )
        .then((res) => {
          finalizeMainBankForm(dispatch, res.payload)
        })
        .catch(() => {
          dispatch(toggleLoading(false))
        })
    }

    if (pending.flow === 'CONFIRM_PAY') {
      const body = { ...pending.confirmPayBody }
      if (choice === 'DCC' && pending.dccQuoteId) {
        applyDccSelectionToPayBody(body, pending, DCC_INFO_CHOICE_ACCEPT)
      }
      return dispatch(
        callAPI({
          dispatch,
          method: 'POST',
          body,
          header: {
            'Salt-Key': pending.saltKey,
          },
          endpoint: 'cashierii/pay/v1/unauth/confirm-pay',
          requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
          successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
          success: function() {},
        })
      )
        .then((res) => {
          finalizeMainBankForm(dispatch, res.payload)
        })
        .catch(() => {
          dispatch(toggleLoading(false))
        })
    }

    dispatch(toggleLoading(false))
    return Promise.resolve()
  }
}

// ------------------------------------
// Actions
// ------------------------------------
var record = false
export function init() {
  // sim测试商户号：200000030906
  // uat测试商户号：200000054800
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          orderToken,
        },
        errControl: true,
        endpoint: 'cashierii/order/v1/unauth/init-trade',
        requestAction: PAY_PAGE_MAIN.INIT_REQUEST,
        successAction: PAY_PAGE_MAIN.INIT_SUCCESS,
        success: function(result) {
          dispatch({
            type: 'SITE_CONFIG_HANDLE_SUCCESS',
            payload: {
              payInfo: result.payInfo,
              orderNo: result.orderNo,
            },
          })
          if (!record) {
            recordCountlyEvent({
              page_name: 'cashier',
              element_name: 'pagerender',
              event_name: 'complete',
              biz_tags: {
                trade_voucher_no: result.orderNo,
                token: orderToken,
              },
            })
            record = true
          }
          sessionStorage.setItem('orderNo', result.orderNo)

          if (result.loginToken) {
            sessionStorage[orderToken] = result.loginToken
            sessionStorage[`${orderToken}-loginType`] = 'customerId'
            dispatch({
              type: 'SITE_CONFIG_HANDLE_SUCCESS',
              payload: {
                loginToken: sessionStorage[orderToken],
                loginType: 'customerId',
              },
            })
            dispatch(init())
          }
          dispatch(queryResult())
          document.addEventListener('visibilitychange', (e) => {
            if (document.visibilityState === 'visible') {
              console.log('document.visibilityState', document.visibilityState)
              clearTimeout(window['query-trade-result-timeout'])
              dispatch(queryResult())
            } else {
              console.log('document.visibilityState', document.visibilityState)
              clearTimeout(window['query-trade-result-timeout'])
            }
          })
        },
      })
    )
  }
}

export function getCardToken(data, saltKey) {
  return (dispatch) => {
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: data,
        header: {
          'Salt-Key': saltKey,
        },
        endpoint: 'cashierii/common/v1/unauth/cache-card-info',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success: function(result) {},
      })
    )
  }
}

export function queryResult() {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          orderToken,
        },
        errControl: true,
        hideProgress: true,
        endpoint: 'cashierii/order/v1/unauth/query-trade-result',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success: function(result) {
          // 处理pay-page/main下面子路由结果查询
          if (!/^\/pay-page\/main/.test(getState().router.location.pathname)) {
            return false
          }
          if (result.payResult === 'S' || result.payResult === 'F') {
            window.location.href = `/pay-page/result?orderToken=${orderToken}`
          } else {
            // 处理浏览器切到后台之后,轮询暂停问题
            window['query-trade-result-timeout'] = setTimeout(() => {
              dispatch(queryResult())
            }, 5000)
          }
        },
      })
    )
  }
}

export function payByCardToekn(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    const saltKey = getUUID()
    dispatch(toggleLoading(true))
      .then(() => updateAliFingerprint())
      .then((res) => dispatch(getAppSalt(saltKey)))
      .then((res) => {
        const { salt } = res.payload
        if (!salt) {
          throw new Error(' ')
        }

        const { cvv, cardNo, expireDate, holderName } = data
        return dispatch(
          getCardToken(
            {
              cvv: encrypt(cvv, salt),
              expireDate: encrypt(expireDate, salt),
              holderName: encrypt(holderName, salt),
              cardNo: encrypt(cardNo.replace(/ /g, ''), salt),
              orderToken: orderToken,
            },
            saltKey
          )
        )
      })
      .then((res) => {
        const { cardToken } = res.payload
        if (!cardToken) {
          throw new Error(' ')
        }
        const { payerEmail, isSaveCard, installmentPlanId } = data
        const payInfo = data.payInfo || _.get(data, 'siteConfig.payInfo') || getState().siteConfig.payInfo
        const body = {
          cardToken,
          orderToken,
          payInfo,
          payerEmail,
          isSaveCard,
        }
        if (installmentPlanId) {
          body.installPlanId = installmentPlanId
        }

        if (!shouldAttemptDccCheck(cardToken)) {
          return dispatch(
            callAPI({
              dispatch: dispatch,
              method: 'POST',
              body,
              endpoint: 'cashierii/pay/v1/unauth/card-token-pay',
              requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
              successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
              success: function(result) {},
            })
          )
        }

        return dispatch(checkDccAvailabilityWithCardToken(orderToken, cardToken)).then((dcc) => {
          if (dcc) {
            dispatch(toggleLoading(false))
            dispatch({
              type: PAY_PAGE_MAIN.SET_DCC_PENDING,
              payload: {
                dccPending: {
                  flow: 'CARD_TOKEN',
                  cardTokenPayBody: body,
                  dccQuoteId: dcc.dccQuoteId,
                  merchantPayAmount: resolveMerchantPayAmountForDcc(data, getState),
                  dccTotalAmount: dcc.dccTotalAmount,
                  exchangeRate: dcc.exchangeRate,
                  markupRate: dcc.markupRate,
                },
              },
            })
            return null
          }
          return dispatch(
            callAPI({
              dispatch: dispatch,
              method: 'POST',
              body,
              endpoint: 'cashierii/pay/v1/unauth/card-token-pay',
              requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
              successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
              success: function(result) {},
            })
          )
        })
      })
      .then((res) => {
        if (!res) {
          return
        }
        finalizeMainBankForm(dispatch, res.payload)
      })
      .catch((res) => {
        dispatch(toggleLoading(false))
      })
  }
}

export function payByCardId(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    const saltKey = getUUID()
    const { cvv, payMethodShortNo, installmentPlanId } = data
    return dispatch(toggleLoading(true))
      .then(() => updateAliFingerprint())
      .then((res) => dispatch(getAppSalt(saltKey)))
      .then((res) => {
        const { salt } = res.payload
        if (!salt) {
          throw new Error(' ')
        }
        const body = {
          orderToken,
          payMethodShortNo,
          cvv: encrypt(cvv, salt),
        }
        if (installmentPlanId) {
          body.installPlanId = installmentPlanId
        }

        if (shouldAttemptDccCheck(payMethodShortNo)) {
          return dispatch(checkDccAvailabilitySaved(orderToken, payMethodShortNo)).then((dcc) => {
            if (dcc) {
              dispatch(toggleLoading(false))
              const merchantPayAmount = resolveMerchantPayAmountForDcc(data, getState)
              dispatch({
                type: PAY_PAGE_MAIN.SET_DCC_PENDING,
                payload: {
                  dccPending: {
                    flow: 'CONFIRM_PAY',
                    confirmPayBody: body,
                    saltKey,
                    dccQuoteId: dcc.dccQuoteId,
                    merchantPayAmount,
                    dccTotalAmount: dcc.dccTotalAmount,
                    exchangeRate: dcc.exchangeRate,
                    markupRate: dcc.markupRate,
                  },
                },
              })
              return null
            }
            return dispatch(
              callAPI({
                dispatch: dispatch,
                method: 'POST',
                body,
                header: {
                  'Salt-Key': saltKey,
                },
                endpoint: 'cashierii/pay/v1/unauth/confirm-pay',
                requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
                successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
                success: function(result) {},
              })
            )
          })
        }

        return dispatch(
          callAPI({
            dispatch: dispatch,
            method: 'POST',
            body,
            header: {
              'Salt-Key': saltKey,
            },
            endpoint: 'cashierii/pay/v1/unauth/confirm-pay',
            requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
            successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
            success: function(result) {},
          })
        )
      })
      .then((res) => {
        if (!res) {
          return
        }
        finalizeMainBankForm(dispatch, res.payload)
      })
      .catch((res) => {
        dispatch(toggleLoading(false))
      })
  }
}

export function applePayValidateURL(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    const validationUrl = new URLSearchParams(window.location.search).get('validationUrl')
    if (validationUrl && data.params) {
      data.params.domainName = validationUrl
    }
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          orderToken,
          ...data,
        },
        endpoint: 'cashierii/pay/v1/unauth/init',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success: function(result) {},
      })
    )
  }
}

export function payByDevice(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          orderToken,
          ...data,
        },
        endpoint: 'cashierii/pay/v1/unauth/confirm-pay',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success: function(result) {
          const { bankForm, paymentOrderNo } = result

          if (bankForm) {
            if (/^http/.test(bankForm)) {
              window.location.href = bankForm
              return
            }

            document.open()
            document.write(bankForm)
            document.close()
            return
          }

          window.location.href = `/pay-page/result?orderToken=${orderToken}&paymentOrderNo=${paymentOrderNo}`
        },
      })
    )
  }
}

export function payWithWallet(data) {
  const { payMethodShortNo } = data.item.payMethod
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          orderToken,
          payMethodShortNo,
        },
        endpoint: 'cashierii/pay/v1/unauth/confirm-pay',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success: function(result) {
          window.open(result.bankForm, '_top')
        },
      })
    )
  }
}

export function toggleLoading(isLoading) {
  return (dispatch) => {
    dispatch({
      type: PAY_PAGE_MAIN.TOGGLE_FETCH_STATUS,
      payload: {
        loading: isLoading,
      },
    })
    return Promise.resolve()
  }
}

export function deleteCard(data) {
  return (dispatch, getState) => {
    const loginToken = getState().siteConfig.loginToken
    return dispatch(
      callAPI({
        dispatch: dispatch,
        method: 'POST',
        body: {
          ...data,
          loginToken,
        },
        endpoint: 'cashierii/order/v1/unauth/delete-pay-method',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success: function(result) {},
      })
    )
  }
}

export function queryInstallPlan(data) {
  return (dispatch, getState) => {
    const orderToken = getState().siteConfig.orderToken
    const saltKey = getUUID()
    // For new cards, encrypt cardNo with salt
    // For saved cards, use payMethodShortNo (ticket) directly as cardNo
    if (data.cardNo && !data.payMethodShortNo) {
      // New card flow - encrypt cardNo with salt
      return dispatch(toggleLoading(true))
        .then(() => updateAliFingerprint())
        .then((res) => dispatch(getAppSalt(saltKey)))
        .then((res) => {
          const { salt } = res.payload
          if (!salt) {
            throw new Error(' ')
          }
          const cardNo = data.cardNo ? data.cardNo.replace(/ /g, '') : null
          return dispatch(
            callAPI({
              dispatch: dispatch,
              method: 'POST',
              body: {
                orderToken,
                cardNo: encrypt(cardNo, salt),
              },
              header: {
                'Salt-Key': saltKey,
              },
              endpoint: 'cashierii/pay/v1/unauth/query-install-plan',
              requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
              successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
              errControl: false,
              hideProgress: true,
              success: function(result) {
                console.log('queryInstallPlan success callback - result:', result)
              },
            })
          )
        })
        .then((res) => {
          dispatch(toggleLoading(false))
          return res
        })
        .catch((res) => {
          dispatch(toggleLoading(false))
          throw res
        })
    } else {
      // Saved card flow - send orderToken, loginToken and payMethodShortNo
      return dispatch(toggleLoading(true))
        .then(() => updateAliFingerprint())
        .then(() => {
          const { payMethodShortNo } = data
          // Get loginToken from sessionStorage using orderToken
          const loginToken = sessionStorage[orderToken]
          return dispatch(
            callAPI({
              dispatch: dispatch,
              method: 'POST',
              body: {
                orderToken,
                loginToken,
                payMethodShortNo,
              },
              endpoint: 'cashierii/pay/v1/unauth/query-install-plan',
              requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
              successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
              errControl: false,
              hideProgress: true,
              success: function(result) {
                console.log('queryInstallPlan success callback - result:', result)
              },
            })
          )
        })
        .then((res) => {
          dispatch(toggleLoading(false))
          return res
        })
        .catch((res) => {
          dispatch(toggleLoading(false))
          throw res
        })
    }
  }
}

export function messagePay(data, form) {
  return (dispatch, getState) => {
    const saltKey = getUUID()
    return dispatch(toggleLoading(true, 'LoginLoading'))
      .then(() => updateAliFingerprint())
      .then((res) => dispatch(verifyOTP({ otpCode: data.otp }, form)))
      .then((res) => {
        if (res?.payload.result !== 'pass') {
                    res?.payload.message && message.error(`${res?.payload.message}`)
                    throw new Error(res?.payload.message)
        }
        return dispatch(getAppSalt(saltKey))
      })
      .then((res) => {
        const { salt } = res.payload
        const { mobileNo, orderToken } = data
        const identifyTicket = _.get(getState().siteConfig, 'identifyHint.identifyTicket')
        const body = { orderToken, identifyTicket }
        body.mobileNo = encrypt(mobileNo, salt)
        return dispatch(
          callAPI({
            dispatch: dispatch,
            method: 'POST',
            header: {
              'Salt-Key': saltKey,
            },
            body,
            endpoint: 'cashierii/pay/v1/unauth/message-pay',
            requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
            successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
            success: (result) => {
              if (result.paymentOrderNo) {
                window.location.href = `/pay-page/result?orderToken=${orderToken}&paymentOrderNo=${result.paymentOrderNo}`
              }
            },
          })
        )
      })
      .catch((e) => {
        dispatch(toggleLoading(false, 'LoginLoading'))
      })
  }
}

export function unauthConfirmPay(data, callback) {
  return (dispatch, getState) => {
    const loginToken = getState().siteConfig.loginToken
    return dispatch(
      callAPI({
        dispatch,
        method: 'POST',
        body: {
          ...data,
          loginToken,
        },
        endpoint: 'cashierii/pay/v1/unauth/confirm-pay',
        requestAction: PAY_PAGE_MAIN.HANDLE_REQUEST,
        successAction: PAY_PAGE_MAIN.HANDLE_SUCCESS,
        success(result) {
          typeof callback === 'function' && callback(null, result)
        },
        error() {
          typeof callback === 'function' && callback(new Error('error'))
        }
      })
    )
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [PAY_PAGE_MAIN.INIT_REQUEST]: (state, action) => Object.assign({}, state, action.payload),
  [PAY_PAGE_MAIN.INIT_SUCCESS]: (state, action) => Object.assign({}, state, action.payload),
  [PAY_PAGE_MAIN.TOGGLE_FETCH_STATUS]: (state, action) => Object.assign({}, state, action.payload),
  [PAY_PAGE_MAIN.SET_DCC_PENDING]: (state, action) =>
    Object.assign({}, state, { dccPending: action.payload.dccPending }),
  [PAY_PAGE_MAIN.CLEAR_DCC_PENDING]: (state) => Object.assign({}, state, { dccPending: null }),
  [PAY_PAGE_MAIN.HANDLE_REQUEST]: (state, action) => state,
  [PAY_PAGE_MAIN.HANDLE_SUCCESS]: (state, action) => state,
}

// ------------------------------------
// Reducer
// ------------------------------------
const initialState = {
  isFetching: true,
  dccPending: null,
}
function reducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

export default function(history) {
  return {
    id: 'payPageMain',
    reducerMap: {
      payPageMainState: reducer,
    },
    // middlewares when this module needed
    middlewares: [apiMiddleware, routerMiddleware(history)],
    // Actions to fire when this module is added/removed
    initialActions: [getThemeVariables(), getCountries()],
    // finalActions: []
  }
}
